import { DrawingModel, type Handle } from "../DrawingModel";
import type { Chart } from "../../chart/Chart";
import { pointInRect, distToRectBorder, distToEllipse, withAlpha } from "../geometry";
import { roundRect } from "./helpers";

const TOL = 6;

export class Rectangle extends DrawingModel {
  type = "rect";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    const x = Math.min(x1, x2),
      y = Math.min(y1, y2),
      w = Math.abs(x2 - x1),
      h = Math.abs(y2 - y1);
    if (this.style.fillAlpha > 0) {
      ctx.fillStyle = withAlpha(this.style.color, this.style.fillAlpha);
      ctx.fillRect(x, y, w, h);
    }
    this.applyStroke(ctx);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
  }
  handles(chart: Chart): Handle[] {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    return [
      { x: x1, y: y1, kind: 0 },
      { x: x2, y: y2, kind: 1 },
      { x: x2, y: y1, kind: 2 },
      { x: x1, y: y2, kind: 3 },
    ];
  }
  moveHandle(kind: number, px: number, py: number, chart: Chart) {
    const a = this.anchors[0],
      b = this.anchors[1];
    const time = chart.timeOfX(px);
    const price = chart.viewport.priceOfY(py);
    if (kind === 0) {
      a.time = time;
      a.price = price;
    } else if (kind === 1) {
      b.time = time;
      b.price = price;
    } else if (kind === 2) {
      b.time = time;
      a.price = price;
    } else if (kind === 3) {
      a.time = time;
      b.price = price;
    }
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    if (this.style.fillAlpha > 0 && pointInRect(px, py, x1, y1, x2, y2)) return true;
    return distToRectBorder(px, py, x1, y1, x2, y2) <= TOL;
  }
}

export class Ellipse extends DrawingModel {
  type = "ellipse";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    const cx = (x1 + x2) / 2,
      cy = (y1 + y2) / 2;
    const rx = Math.abs(x2 - x1) / 2,
      ry = Math.abs(y2 - y1) / 2;
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    if (this.style.fillAlpha > 0) {
      ctx.fillStyle = withAlpha(this.style.color, this.style.fillAlpha);
      ctx.fill();
    }
    this.applyStroke(ctx);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  handles(chart: Chart): Handle[] {
    const [a, b] = this.anchors;
    return [
      { x: this.X(a, chart), y: this.Y(a, chart), kind: 0 },
      { x: this.X(b, chart), y: this.Y(b, chart), kind: 1 },
    ];
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    const x1 = this.X(a, chart),
      y1 = this.Y(a, chart),
      x2 = this.X(b, chart),
      y2 = this.Y(b, chart);
    const cx = (x1 + x2) / 2,
      cy = (y1 + y2) / 2;
    const rx = Math.abs(x2 - x1) / 2,
      ry = Math.abs(y2 - y1) / 2;
    if (this.style.fillAlpha > 0) {
      const nx = (px - cx) / rx,
        ny = (py - cy) / ry;
      if (nx * nx + ny * ny <= 1) return true;
    }
    return distToEllipse(px, py, cx, cy, rx, ry) <= TOL;
  }
}
